<script setup lang="ts">
import { camelCase, get } from 'lodash';
import { useRoute } from 'vue-router';
import { provide, reactive, ref, computed, watchEffect } from 'vue';

import AppTheme from './AppTheme.vue';
import AppLayout from './AppLayout.vue';
import { type App } from '../classes/app';
import type { LayoutSections } from '../types/layoutOptions';

performance.mark('app-base-setup');

type Layouts = Record<string, LayoutSections>;

const props = defineProps<{
  app: App;
  layouts?: Layouts;
  loading?: boolean;
}>();

const standardLayout = computed<LayoutSections>(
  () =>
    props.layouts?.standard || {
      announcement: { show: true, height: '40px' },
      header: {
        show: false,
        fixed: true,
        overlay: false,
        width: '100%',
        height: '4rem',
        content: true,
        logoMaxWidth: '220px',
        centerLogo: false,
        logo: true,
      },
      sidebar: {
        show: false,
        fixed: true,
        width: '220px',
      },
      content: { show: true, head: true, width: '100%' },
      footer: { show: false },
      subFooter: { show: false },
      navBar: { show: false, height: '3rem' },
    },
);

const layoutName = ref<keyof Layouts>('standard');
const layoutOptions: LayoutSections = reactive({
  ...standardLayout.value,
  announcement: false,
  header: false,
  sidebar: false,
  content: false,
  footer: false,
  subFooter: false,
  navBar: false,
});

provide('app', props.app);

const route = useRoute();

watchEffect(() => {
  const metaLayout =
    typeof route.meta.layout === 'function' ? route.meta.layout(route) : route.meta.layout;

  layoutName.value = camelCase(metaLayout || 'blank') as keyof Layouts;

  Object.assign(layoutOptions, get(props.layouts, layoutName.value, standardLayout));
});
</script>

<template>
  <AppTheme v-if="app.theme.value" :theme="app.theme.value">
    <AppLayout :layout="layoutOptions">
      <template #announcement="{ options }">
        <slot name="announcement" v-bind="{ type: layoutName, options }" />
      </template>

      <template #header="{ options }">
        <slot name="header" v-bind="{ type: layoutName, options }" />
      </template>

      <template #sidebar="{ options }">
        <slot name="sidebar" v-bind="{ type: layoutName, options }" />
      </template>

      <template #content="{ options }">
        <slot name="before-content" v-bind="{ type: layoutName, options }" />
        <slot name="content" v-bind="{ type: layoutName, options }">
          <router-view />
        </slot>
        <slot name="after-content" v-bind="{ type: layoutName, options }" />
      </template>

      <template #footer="{ options }">
        <slot name="footer" v-bind="{ type: layoutName, options }" />
      </template>

      <template #subfooter="{ options }">
        <slot name="subfooter" v-bind="{ type: layoutName, options }" />
      </template>

      <template #nav-bar="{ options }">
        <slot name="nav-bar" v-bind="{ type: layoutName, options }" />
      </template>

      <template #global="{ options }">
        <slot name="global" v-bind="{ type: layoutName, options }" />
      </template>
    </AppLayout>
    <Transition name="loader">
      <div
        v-if="loading"
        class="fixed top-0 left-0 bottom-0 right-0 bg-[--tml-page-background-color] text-[--tml-text-color] z-[200]"
      >
        <slot name="loader" />
      </div>
    </Transition>
  </AppTheme>
</template>

<style scoped>
.loader-leave-active,
.loader-enter-active {
  transition: all 0.25s ease-out;
  opacity: 1;
}
.loader-enter-from {
  opacity: 0;
}
.loader-leave-to {
  transition-delay: 0.25s;
  opacity: 0;
}
</style>
